import * as React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import FirstStep from "src/components/CreateGame/CreateGame.FirstStep";
import SecondStep from "src/components/CreateGame/CreateGame.SecondStep";
import { displayErrorToast } from "src/components/shared/Toasts/Toast";
import { ToastIds } from "src/enums/errorToastIds";
import { ShowCardsOption, ShowCardsOptions } from "../../enums/showCards";
import { VotingOption } from "../../enums/vote/votingSystems";
import { useGameContext } from "../../game/useGameContext";
import {
  ContentWrapper,
  FloatingNerdyBird,
  Form,
  NextStepButton,
  Step,
  Stepper,
  StepSeparator,
} from "../CreateGame/CreateGame.components";
import PageWrapper from "../shared/PageWrapper";

export interface IFormValues {
  gameName: string;
  votingSystem: VotingOption;
  showCards: ShowCardsOption;
  userName: string;
  isSpectator: boolean;
}

const CreateGame: React.FunctionComponent = () => {
  const [step, setStep] = React.useState<number>(0);
  const [showGamesNameError, setShowGamesNameError] =
    React.useState<boolean>(false);
  const { gameState, actionHandlers } = useGameContext();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<IFormValues>();

  const { votingSystems } = gameState;

  const onSubmit: SubmitHandler<IFormValues> = async data => {
    if (step === 0) {
      setStep(1);
    } else {
      try {
        const response = await actionHandlers.createGame({
          user: {
            name: data.userName,
            isSpectator: data.isSpectator,
          },
          game: {
            name: data.gameName,
            votingSystem: data.votingSystem.value,
            showCards: data.showCards.value,
          },
        });
        if (response.error) {
          displayErrorToast(
            `Couldn't create game ${response.error.message}`,
            ToastIds.CREATE_GAME_FAILED
          );
        } else {
          navigate(`/game/${response.data.game.id}`);
        }
      } catch (e) {
        displayErrorToast("Couldn't create game", ToastIds.CREATE_GAME_FAILED);
      }
    }
  };

  const votingOptions =
    votingSystems?.map(system => ({
      label: system.description,
      value: system.name,
    })) ?? [];

  const ableToBackToStep1 = step > 0;

  const backToStep1 = () => {
    if (!ableToBackToStep1) {
      return;
    }
    setStep(0);
  };

  React.useEffect(() => {
    if (errors.gameName?.message) {
      setShowGamesNameError(true);
    } else {
      setShowGamesNameError(false);
    }
  }, [errors.gameName]);

  React.useEffect(() => {
    if (votingSystems && votingSystems[0]) {
      setValue("votingSystem", {
        label: votingSystems[0].description,
        value: votingSystems[0].name,
      });
    }
    setValue("showCards", ShowCardsOptions[0]);
  }, [votingSystems, setValue]);

  return (
    <>
      <PageWrapper>
        <ContentWrapper>
          <h1>Create a game</h1>
          <Stepper>
            <p>Steps</p>
            <Step
              active={step >= 0}
              clickable={ableToBackToStep1}
              onClick={backToStep1}>
              1
            </Step>
            <StepSeparator />
            <Step active={step >= 1}>2</Step>
          </Stepper>
          <Form onSubmit={handleSubmit(onSubmit)} aria-label="create-game-form">
            {step === 0 && (
              <FirstStep
                isGameNameErrorVisible={showGamesNameError}
                onGameNameErrorTooltipClose={() => setShowGamesNameError(false)}
                register={register}
                errors={errors}
                control={control}
                votingOptions={votingOptions}
                votingSystems={votingSystems}
              />
            )}
            {step === 1 && <SecondStep register={register} errors={errors} />}
            <NextStepButton
              disabled={!votingSystems || votingSystems[0] === undefined}
              themeColor="buttonPrimary">
              {step === 0 && "Continue"}
              {step === 1 && "Save and create"}
            </NextStepButton>
          </Form>
        </ContentWrapper>
      </PageWrapper>
      <FloatingNerdyBird />
    </>
  );
};

export default CreateGame;
